import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { parseApiError } from '@saas-kit/lib';
import { Button, ConfirmDialog, toast } from '@saas-kit/ui';

import type { Role } from './api';
import { useRoleMutations } from './useRoleMutations';

interface Props {
  role: Role;
  onDeleted: () => void;
  onError: (i18nKey: string) => void;
}

export function RoleDeleteButton({
  role,
  onDeleted,
  onError,
}: Props): JSX.Element {
  const { t } = useTranslation('rbac');
  const { remove } = useRoleMutations();
  const [confirming, setConfirming] = useState(false);

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        onClick={() => setConfirming(true)}
      >
        {t('editor.delete')}
      </Button>
      {confirming ? (
        <ConfirmDialog
          title={t('editor.confirm_delete_title')}
          body={t('editor.confirm_delete_body')}
          confirmLabel={remove.isPending ? t('editor.deleting') : t('editor.delete')}
          cancelLabel={t('editor.cancel')}
          variant="destructive"
          isBusy={remove.isPending}
          onCancel={() => {
            if (!remove.isPending) setConfirming(false);
          }}
          onConfirm={() =>
            remove.mutate(role.id, {
              onSuccess: () => {
                setConfirming(false);
                onDeleted();
                toast.success(t('toasts.role_deleted'));
              },
              onError: (err) => {
                const key = parseApiError(err).i18nKey;
                onError(key);
                toast.error(t(key, key));
              },
            })
          }
        />
      ) : null}
    </>
  );
}
